import styled, { css } from 'styled-components'
import { device, fonts } from '../../../../styles/GlobalVariables'
import { IPlayerStyle } from '../type'

/** loading shorter than this shows no spinner */
const SPINNER_DELAY_MS = 600

export const PlayerDiv = styled.div<{
  showNextVideoTip: boolean
  seekBarColor?: string
  playerStyle?: IPlayerStyle
  // Stacked top to bottom: each layer shows through while the one above it
  // is still loading or failed to load.
  posters?: string[]
  /** loaded ahead, not playing yet */
  $lookAhead?: boolean
}>`
  ${props =>
    props.$lookAhead &&
    css`
      /* no big Kaltura play button on the slide that slides in during a swipe */
      .playkit-pre-playback-play-overlay {
        display: none !important;
      }
    `}

  aspect-ratio: 9/16;
  width: 100%;
  height: calc(100dvh - env(safe-area-inset-bottom)); // was auto
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-image: ${props =>
    props.posters?.map(src => `url('${src}')`).join(', ') || 'none'};

  /* Once the clip (or an ad) has drawn its first picture (data-drawn, set by
     the player) the box is plain black. The poster is cut to fill the box and
     the clip is fitted inside it: wherever the box is not the clip's shape (a
     tablet, a wide window, a clip not quite 9:16, a landscape ad) a poster
     still there shows beside the clip as a second picture. An attribute, not
     a prop: no new class to build while the clip starts. */
  &[data-drawn] {
    background-image: none;
    background-color: #000;
  }

  /* Kaltura paints these black from setup (its own poster stays black until
     it has fetched the image), and so does a style the tag manager adds once
     metadata loads: see-through, so the box behind them is the stage (the
     poster until the clip draws, black after) */
  .kaltura-player-container,
  .playkit-player .playkit-black-cover,
  .playkit-player .playkit-poster,
  .playkit-player .playkit-video-player {
    background-color: transparent;
  }

  ${device.medium} {
    position: relative;
    width: auto;
    height: 80vh; // for some reason 90vh doesn't work and make the video not pressable
    border-radius: ${props =>
      props.playerStyle?.borderCorner === 'rounded' ? '10px' : '0px'};
  }

  .playkit-player {
    /* Kaltura sets cursor: none until metadata loads or an ad break starts */
    cursor: auto;

    .playkit-bottom-bar {
      z-index: 9999 !important;
      background: unset !important;
      /* ${
        props =>
          props.showNextVideoTip
            ? 'margin-bottom: 64px !important;' // should be 34(next item box) + 20(gap) + 10(bottom) = 64px
            : 'margin-bottom: 10px !important;' // should be 10px
      }; */
      padding: 0 ${props => props.playerStyle?.overlayPaddingMobile ?? 16}px 4px !important;
      margin-bottom: ${props =>
        (props.playerStyle?.overlayPaddingMobile ?? 10) +
        (props.playerStyle?.seekBar == 'stacked' ? 17 : 0) +
        (props.showNextVideoTip ? 34 : 0)}px !important;

      ${device.small} {
        padding: 0 ${props => props.playerStyle?.overlayPadding ?? 8}px 4px !important;
        margin-bottom: ${props =>
          (props.playerStyle?.overlayPadding ?? 30) +
          (props.playerStyle?.seekBar == 'stacked' ? 17 : 0)}px !important;
      }

      .playkit-time-display {
        display: none;
      }

      .playkit-controls-container {
        display: none;
      }

      #scrubber-container {
        display: none;
      }

      .playkit-bottom-bar-area {
        display: flex;
        flex-direction: column;

        .playkit-seek-bar {
          width: 100%;
          margin: auto;

          .playkit-progress-bar {
            height: 6px !important;

            .playkit-buffered {
              height: 5px !important; // 6px - 1px border
              background-color: transparent;
            }
          }
        }
      }
    }
  }

  .playkit-icon-pause,
  .playkit-top-bar,
  .playkit-right-controls {
    display: none !important;
  }

  /* Kaltura's spinner over the poster while the clip loads or stalls (its
     backdrop is transparent), shown only after a moment: a fast start shows
     none, a slow link does not look frozen */
  .playkit-loading-backdrop .playkit-spinner-container {
    opacity: 0;
  }
  .playkit-loading-backdrop.playkit-show .playkit-spinner-container {
    opacity: 1;
    transition: opacity 0.2s ease ${SPINNER_DELAY_MS}ms;
  }

  /**
   * RESH13-1138 - where the overlay draws its own play button, Kaltura's
   * central one has to go, otherwise the two sit on top of each other in the
   * middle of the player. The reels feed has no button of its own and keeps
   * Kaltura's.
   */
  ${props =>
    props.playerStyle?.showCenterPlayButton &&
    css`
      /* while playing or paused - the play/pause control in the middle */
      .playkit-center-playback-controls,
      /* before the first start - the button drawn over the poster */
      .playkit-pre-playback-play-overlay,
      .playkit-pre-playback-play-button {
        display: none !important;
      }
    `}

  .playkit-progress {
    background-color: ${props => props.seekBarColor} !important;
    height: 5px !important; // 6px - 1px border
  }

  .playkit-overlay-action {
    background: ${props =>
      props.playerStyle?.ownShade
        ? 'none'
        : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 59.99%, #000 103.03%)'} !important;
  }

  .playkit-seek-bar {
    ${props => props.playerStyle?.seekBar == 'linear' && 'margin: 0px 50px;'}
  }

  .custom-seekbar {
    display: flex;
    flex-direction: row;
    direction: rtl;
    justify-content: space-between;
    width: 100%;
    ${props => props.playerStyle?.seekBar == 'linear' && 'bottom: -3px;'}
  }

  .top-bar {
    position: absolute;
    width: 100%;
    bottom: -3px;
  }

  .time {
    color: #fff;
    font-family: ${fonts.narkis};
    font-size: ${props => props.playerStyle?.seekBarTimeSize ?? 16}px;
    font-weight: 400;
    text-align: right;
    line-height: normal;
    letter-spacing: -0.72px;
  }

  .playkit-right-controls,
  .playkit-left-controls {
    .playkit-tooltip {
      display: none !important;
    }
  }

  /* Remove focus outline from play/pause button */

  .playkit-control-button-container.playkit-control-play-pause {
    .playkit-control-button:focus {
      outline: none !important;
      outline-offset: none !important;
      box-shadow: none !important;
    }
  }

  ${props =>
    props.playerStyle?.fill &&
    css`
      /* the viewer sizes the box and draws every control itself */
      width: 100%;
      height: 100%;
      aspect-ratio: auto;
      border-radius: 0;

      ${device.medium} {
        width: 100%;
        height: 100%;
        border-radius: 0;
      }

      .playkit-video-player video {
        object-fit: cover;
      }

      /* none of Kaltura's own interface: the viewer's loading frame covers its
         controls as well (onLoading), which a spinner inside the clip cannot */
      .playkit-gui-area {
        visibility: hidden;
      }
      .playkit-gui-area,
      .playkit-gui-area * {
        pointer-events: none !important;
      }
      .playkit-pre-playback-play-overlay {
        display: none !important;
      }

      /* a style the tag manager adds (div.playkit-metadata-loaded
         div.playkit-black-cover and the poster and video layers, black)
         outranked the rule above: the clip's poster and first frame show,
         not a black stage, until it plays */
      .playkit-player.playkit-metadata-loaded .playkit-black-cover,
      .playkit-player.playkit-metadata-loaded .playkit-poster,
      .playkit-player.playkit-metadata-loaded .playkit-video-player {
        background-color: transparent;
      }

      /* Kaltura places its layers for a left to right page: inside the RTL
         viewer WebKit put the clip 1px right at some widths */
      .kaltura-player-container {
        direction: ltr;
      }
    `}
`
